import React, { useEffect, useState } from 'react';
import {
  User, Bell, LogOut, ChevronRight, MessageCircle, Shield, Lock,
  CheckCircle2, UserCheck, Mail, Activity, HelpCircle, FileText, X, Eye, EyeOff, Calendar,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { BACKEND_URL } from '../../services/config';

/**
 * ProfileScreen v2 — Fully functional account settings.
 * Fixes: dead buttons, security gap, missing stats, missing support links.
 */

const Palette = {
  text: '#0F172A', sub: '#475569', muted: '#94A3B8', line: '#E2E8F0', bg: '#F8FAFC',
  accent: '#F5A623', success: '#10B981', danger: '#EF4444',
};

// Simple modal wrapper
const Modal = ({ open, onClose, title, children, testId }) => {
  if (!open) return null;
  return (
    <div
      data-testid={testId}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
      }}
    >
      <div style={{
        width: '100%', maxWidth: '440px',
        background: '#FFF', borderRadius: '20px', padding: '22px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: Palette.text }}>{title}</h3>
          <button
            data-testid={`${testId}-close`}
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: Palette.muted }}
          ><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '12px' }}>
    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: Palette.sub, marginBottom: '6px' }}>
      {label}
    </label>
    {children}
  </div>
);

const Input = React.forwardRef((props, ref) => (
  <input
    ref={ref}
    {...props}
    style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1px solid ${Palette.line}`, background: Palette.bg,
      fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', ...(props.style || {}),
    }}
  />
));
Input.displayName = 'Input';

const PrimaryButton = ({ children, onClick, disabled, testId, danger }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    style={{
      width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
      background: danger ? 'linear-gradient(135deg,#EF4444,#DC2626)' : 'linear-gradient(135deg,#F5A623,#E8951A)',
      color: '#FFF', fontSize: '0.95rem', fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.65 : 1,
      boxShadow: danger ? '0 4px 12px rgba(239,68,68,0.3)' : '0 4px 12px rgba(245,166,35,0.3)',
      transition: 'transform 0.12s ease',
    }}
    onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = 'scale(0.98)'; }}
    onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
  >{children}</button>
);

const Alert = ({ kind = 'info', children, testId }) => {
  const map = {
    error:   { bg: 'rgba(239,68,68,0.08)', color: '#B91C1C' },
    success: { bg: 'rgba(16,185,129,0.10)', color: '#047857' },
    info:    { bg: 'rgba(59,130,246,0.08)', color: '#1D4ED8' },
  };
  const s = map[kind] || map.info;
  return (
    <div data-testid={testId} style={{
      padding: '10px 12px', borderRadius: '10px',
      background: s.bg, color: s.color, fontSize: '0.85rem', marginBottom: '12px',
    }}>{children}</div>
  );
};

const ProfileScreen = () => {
  const { user, logout, updateUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [modal, setModal] = useState(null); // 'edit' | 'password' | 'telegram' | 'notif' | 'help' | 'privacy' | 'terms'

  // Edit Profile state
  const [form, setForm] = useState({ name: '', phone: '' });
  const [formSaving, setFormSaving] = useState(false);
  const [formStatus, setFormStatus] = useState({ error: '', success: '' });

  // Password state
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdShow, setPwdShow] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdStatus, setPwdStatus] = useState({ error: '', success: '' });

  useEffect(() => {
    const token = localStorage.getItem('fira_auth_token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/auth/me/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setStats(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (modal === 'edit') {
      setForm({ name: user?.name || '', phone: user?.phone || '' });
      setFormStatus({ error: '', success: '' });
    }
    if (modal === 'password') {
      setPwd({ current: '', next: '', confirm: '' });
      setPwdStatus({ error: '', success: '' });
    }
  }, [modal, user]);

  const hasBroker = !!(user?.broker_account && user.broker_account.trim?.().length > 0);
  const memberSinceLabel = stats?.member_since_iso
    ? new Date(stats.member_since_iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : '—';

  const handleSaveProfile = async () => {
    setFormSaving(true);
    setFormStatus({ error: '', success: '' });
    try {
      const token = localStorage.getItem('fira_auth_token');
      const res = await fetch(`${BACKEND_URL}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: form.name, phone: form.phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormStatus({ error: data.detail || 'Failed to save', success: '' });
      } else {
        setFormStatus({ error: '', success: 'Profile updated successfully' });
        if (data.user && updateUser) updateUser(data.user);
        setTimeout(() => setModal(null), 900);
      }
    } catch {
      setFormStatus({ error: 'Network error. Try again.', success: '' });
    } finally {
      setFormSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPwdStatus({ error: '', success: '' });
    if (pwd.next.length < 8) {
      setPwdStatus({ error: 'New password must be at least 8 characters', success: '' });
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setPwdStatus({ error: 'New password and confirmation do not match', success: '' });
      return;
    }
    setPwdSaving(true);
    try {
      const token = localStorage.getItem('fira_auth_token');
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: pwd.current, new_password: pwd.next }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPwdStatus({ error: data.detail || 'Failed to change password', success: '' });
      } else {
        setPwdStatus({ error: '', success: 'Password changed successfully' });
        setTimeout(() => setModal(null), 1100);
      }
    } catch {
      setPwdStatus({ error: 'Network error. Try again.', success: '' });
    } finally {
      setPwdSaving(false);
    }
  };

  const MenuItem = ({ icon: Icon, label, desc, color, onClick, danger, testId, trailing }) => (
    <button
      data-testid={testId}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '14px', width: '100%',
        padding: '14px 16px', background: '#FFF', border: 'none', borderRadius: '14px',
        cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s ease',
        marginBottom: '6px',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#FFF'; }}
    >
      <div style={{
        width: '40px', height: '40px', borderRadius: '12px',
        background: danger ? 'rgba(239,68,68,0.08)' : `${color || Palette.accent}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={20} color={danger ? Palette.danger : (color || Palette.accent)} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: danger ? Palette.danger : Palette.text }}>
          {label}
        </div>
        {desc && <div style={{ fontSize: '11px', color: Palette.muted, marginTop: '2px' }}>{desc}</div>}
      </div>
      {trailing || <ChevronRight size={16} color="#CBD5E1" />}
    </button>
  );

  const SectionLabel = ({ children }) => (
    <div style={{
      fontSize: '11px', fontWeight: 700, color: Palette.muted,
      textTransform: 'uppercase', letterSpacing: '0.12em',
      padding: '0 4px', marginBottom: '8px', marginTop: '4px',
    }}>{children}</div>
  );

  const StatChip = ({ icon: Icon, label, value }) => (
    <div style={{
      flex: 1, background: '#FFF', borderRadius: '14px', padding: '12px 10px',
      border: `1px solid ${Palette.line}`, textAlign: 'center',
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
        <Icon size={16} color={Palette.accent} />
      </div>
      <div style={{ fontSize: '16px', fontWeight: 700, color: Palette.text, fontFamily: 'Poppins, sans-serif' }}>
        {value}
      </div>
      <div style={{ fontSize: '10px', color: Palette.muted, marginTop: '2px' }}>{label}</div>
    </div>
  );

  return (
    <div data-testid="profile-screen" style={{ fontFamily: 'Inter, sans-serif', maxWidth: '480px', margin: '0 auto' }}>
      {/* Header Card */}
      <div style={{
        background: 'linear-gradient(180deg, #FFFBF2 0%, #FFFFFF 100%)',
        borderRadius: '20px', padding: '24px', border: `1px solid ${Palette.line}`,
        textAlign: 'center', marginBottom: '16px',
      }}>
        <div style={{
          width: '72px', height: '72px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #F5A623, #E8951A)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 12px', fontSize: '28px', fontWeight: 700, color: '#FFF',
          fontFamily: 'Poppins', boxShadow: '0 8px 24px rgba(245,166,35,0.25)',
        }}>
          {(user?.name || 'U').charAt(0).toUpperCase()}
        </div>
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: Palette.text, margin: '0 0 4px 0', fontFamily: 'Poppins' }}>
          {user?.name || 'User'}
        </h2>
        <p style={{ fontSize: '13px', color: Palette.muted, margin: 0 }}>{user?.email || ''}</p>

        {hasBroker ? (
          <div data-testid="profile-badge-verified" style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '10px',
            padding: '4px 12px', borderRadius: '20px', background: 'rgba(16,185,129,0.10)',
            fontSize: '12px', fontWeight: 600, color: '#047857',
          }}>
            <CheckCircle2 size={12} /> Verified Member
          </div>
        ) : (
          <div data-testid="profile-badge-member" style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '10px',
            padding: '4px 12px', borderRadius: '20px', background: 'rgba(100,116,139,0.10)',
            fontSize: '12px', fontWeight: 600, color: Palette.sub,
          }}>
            <UserCheck size={12} /> Member
          </div>
        )}
      </div>

      {/* Stats Strip */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }} data-testid="profile-stats-strip">
        <StatChip icon={Calendar} label="Member since" value={memberSinceLabel} />
        <StatChip icon={Activity} label="Signals used" value={stats?.signals_used ?? '—'} />
        <StatChip icon={MessageCircle} label="Telegram" value={stats?.telegram_connected ? 'On' : 'Off'} />
      </div>

      {/* Account */}
      <SectionLabel>Account</SectionLabel>
      <MenuItem testId="menu-edit-profile" icon={User} label="Edit Profile" desc="Name, phone" color="#6366F1" onClick={() => setModal('edit')} />
      <MenuItem testId="menu-change-password" icon={Lock} label="Change Password" desc="Secure your account" color="#0EA5E9" onClick={() => setModal('password')} />
      <MenuItem testId="menu-telegram" icon={MessageCircle} label="Telegram"
        desc={stats?.telegram_connected ? 'Connected' : 'Connect for auto-alerts'}
        color="#0891B2" onClick={() => setModal('telegram')}
        trailing={stats?.telegram_connected
          ? <span style={{ fontSize: '10px', fontWeight: 700, color: Palette.success, padding: '3px 8px', borderRadius: '999px', background: 'rgba(16,185,129,0.10)' }}>ACTIVE</span>
          : <ChevronRight size={16} color="#CBD5E1" />}
      />
      <MenuItem testId="menu-notifications" icon={Bell} label="Notifications" desc="Alert preferences" color="#F5A623" onClick={() => setModal('notif')} />

      {/* Support */}
      <div style={{ marginTop: '12px' }}><SectionLabel>Support & Legal</SectionLabel></div>
      <MenuItem testId="menu-help" icon={HelpCircle} label="Help & Support" desc="Reach our admin team" color="#10B981" onClick={() => setModal('help')} />
      <MenuItem testId="menu-privacy" icon={Shield} label="Privacy Policy" color="#64748B" onClick={() => setModal('privacy')} />
      <MenuItem testId="menu-terms" icon={FileText} label="Terms of Service" color="#64748B" onClick={() => setModal('terms')} />

      {/* Logout — subtle */}
      <div style={{ marginTop: '16px' }}>
        <button
          data-testid="menu-logout"
          onClick={logout}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            width: '100%', padding: '13px 16px', background: '#FFF',
            border: `1px solid ${Palette.line}`, borderRadius: '14px',
            color: Palette.danger, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.05)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#FFF'; e.currentTarget.style.borderColor = Palette.line; }}
        >
          <LogOut size={16} /> Logout
        </button>
      </div>

      <div style={{ textAlign: 'center', marginTop: '18px', fontSize: '11px', color: '#CBD5E1' }}>
        MBE Signal AI v11.6 • Trade Engine
      </div>

      {/* ===== Modals ===== */}

      {/* Edit Profile */}
      <Modal open={modal === 'edit'} onClose={() => setModal(null)} title="Edit Profile" testId="modal-edit-profile">
        {formStatus.error && <Alert kind="error" testId="edit-profile-error">{formStatus.error}</Alert>}
        {formStatus.success && <Alert kind="success" testId="edit-profile-success">{formStatus.success}</Alert>}
        <Field label="Email (locked)">
          <Input data-testid="edit-email" value={user?.email || ''} disabled style={{ opacity: 0.6 }} />
        </Field>
        <Field label="Full name">
          <Input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
        </Field>
        <Field label="Phone (optional)">
          <Input data-testid="edit-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60..." />
        </Field>
        <PrimaryButton testId="edit-save" onClick={handleSaveProfile} disabled={formSaving}>
          {formSaving ? 'Saving…' : 'Save changes'}
        </PrimaryButton>
      </Modal>

      {/* Change Password */}
      <Modal open={modal === 'password'} onClose={() => setModal(null)} title="Change Password" testId="modal-change-password">
        {pwdStatus.error && <Alert kind="error" testId="password-error">{pwdStatus.error}</Alert>}
        {pwdStatus.success && <Alert kind="success" testId="password-success">{pwdStatus.success}</Alert>}
        <Field label="Current password">
          <div style={{ position: 'relative' }}>
            <Input data-testid="password-current" type={pwdShow ? 'text' : 'password'} value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} style={{ paddingRight: '44px' }} />
            <button type="button" onClick={() => setPwdShow(!pwdShow)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: Palette.muted }}>
              {pwdShow ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>
        <Field label="New password (min 8)">
          <Input data-testid="password-new" type={pwdShow ? 'text' : 'password'} value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} />
        </Field>
        <Field label="Confirm new password">
          <Input data-testid="password-confirm" type={pwdShow ? 'text' : 'password'} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
        </Field>
        <PrimaryButton testId="password-save" onClick={handleChangePassword} disabled={pwdSaving || !pwd.current || !pwd.next}>
          {pwdSaving ? 'Saving…' : 'Update password'}
        </PrimaryButton>
      </Modal>

      {/* Telegram */}
      <Modal open={modal === 'telegram'} onClose={() => setModal(null)} title="Telegram Auto-Alerts" testId="modal-telegram">
        {stats?.telegram_connected ? (
          <Alert kind="success">Your Telegram is connected. You'll receive real-time signal alerts.</Alert>
        ) : (
          <>
            <p style={{ fontSize: '13px', color: Palette.sub, lineHeight: 1.6, margin: '0 0 14px' }}>
              Get signal alerts delivered instantly to your Telegram. Setup takes 30 seconds.
            </p>
            <ol style={{ fontSize: '13px', color: Palette.text, lineHeight: 1.7, paddingLeft: '20px', margin: '0 0 14px' }}>
              <li>Open our bot on Telegram</li>
              <li>Send <code style={{ background: Palette.bg, padding: '1px 6px', borderRadius: '6px' }}>/start</code></li>
              <li>Paste your email to link your account</li>
            </ol>
          </>
        )}
        <a
          data-testid="telegram-bot-link"
          href="https://t.me/MBESignalAIbot"
          target="_blank" rel="noopener noreferrer"
          style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
            padding: '14px', borderRadius: '12px', textDecoration: 'none',
            background: 'linear-gradient(135deg,#0EA5E9,#0284C7)', color: '#FFF', fontWeight: 700,
          }}
        >
          <MessageCircle size={16} /> Open MBE Signal Bot
        </a>
        <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: Palette.muted }}>
          Need help? PM admin <a href="https://t.me/zackmbe" target="_blank" rel="noopener noreferrer" style={{ color: Palette.accent, fontWeight: 600 }}>@zackmbe</a>
        </div>
      </Modal>

      {/* Notifications */}
      <Modal open={modal === 'notif'} onClose={() => setModal(null)} title="Notification Preferences" testId="modal-notifications">
        <p style={{ fontSize: '13px', color: Palette.sub, margin: '0 0 12px' }}>
          Choose where you'd like to receive signal alerts.
        </p>
        {[
          { icon: MessageCircle, label: 'Telegram Alerts', status: stats?.telegram_connected ? 'Connected' : 'Setup required', active: !!stats?.telegram_connected },
          { icon: Mail, label: 'Email Digest', status: 'Daily summary at 8PM', active: true },
          { icon: Bell, label: 'Push Notifications', status: 'Coming soon', active: false },
        ].map((row, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '12px', border: `1px solid ${Palette.line}`, borderRadius: '12px', marginBottom: '8px',
          }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${Palette.accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <row.icon size={16} color={Palette.accent} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: Palette.text }}>{row.label}</div>
              <div style={{ fontSize: '11px', color: Palette.muted }}>{row.status}</div>
            </div>
            <span style={{ fontSize: '10px', fontWeight: 700, color: row.active ? Palette.success : Palette.muted, padding: '3px 8px', borderRadius: '999px', background: row.active ? 'rgba(16,185,129,0.10)' : 'rgba(100,116,139,0.10)' }}>
              {row.active ? 'ACTIVE' : 'OFF'}
            </span>
          </div>
        ))}
        <p style={{ fontSize: '11px', color: Palette.muted, margin: '10px 0 0' }}>
          Fine-grained toggle controls coming soon.
        </p>
      </Modal>

      {/* Help */}
      <Modal open={modal === 'help'} onClose={() => setModal(null)} title="Help & Support" testId="modal-help">
        <p style={{ fontSize: '13px', color: Palette.sub, lineHeight: 1.6, margin: '0 0 14px' }}>
          Reach us directly on Telegram for the fastest response. We reply within a few hours on trading days.
        </p>
        <a href="https://t.me/zackmbe" target="_blank" rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            padding: '14px', borderRadius: '12px', textDecoration: 'none',
            background: 'linear-gradient(135deg,#F5A623,#E8951A)', color: '#FFF', fontWeight: 700,
          }}
        >
          <MessageCircle size={16} /> Message @zackmbe
        </a>
        <a href="mailto:hello@mbesignal.com"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            padding: '12px', borderRadius: '12px', textDecoration: 'none', marginTop: '8px',
            background: '#FFF', border: `1px solid ${Palette.line}`, color: Palette.text, fontWeight: 600, fontSize: '13px',
          }}
        >
          <Mail size={16} /> hello@mbesignal.com
        </a>
      </Modal>

      {/* Privacy */}
      <Modal open={modal === 'privacy'} onClose={() => setModal(null)} title="Privacy Policy" testId="modal-privacy">
        <div style={{ fontSize: '13px', color: Palette.sub, lineHeight: 1.6 }}>
          <p><strong>Last updated:</strong> Feb 2026</p>
          <p>MBE Signal AI collects your email, name, optional phone number, and trading activity solely to operate the service and deliver signal alerts.</p>
          <p><strong>Data we store</strong>: account info, signal history, Telegram chat ID (if connected), device metadata used for security.</p>
          <p><strong>We do not</strong>: sell your data to third parties, share your personal info with advertisers, or access your broker account funds.</p>
          <p><strong>Retention</strong>: Account data kept while your account is active. Trading history kept for up to 24 months for AI improvement.</p>
          <p><strong>Rights</strong>: You can request data export or account deletion by emailing <a href="mailto:hello@mbesignal.com" style={{ color: Palette.accent }}>hello@mbesignal.com</a>.</p>
        </div>
      </Modal>

      {/* Terms */}
      <Modal open={modal === 'terms'} onClose={() => setModal(null)} title="Terms of Service" testId="modal-terms">
        <div style={{ fontSize: '13px', color: Palette.sub, lineHeight: 1.6 }}>
          <p><strong>Last updated:</strong> Feb 2026</p>
          <p><strong>Signals are educational, not financial advice.</strong> All trading involves risk. Past performance does not guarantee future results. You are responsible for your own trading decisions and outcomes.</p>
          <p>You agree to (a) keep your credentials confidential, (b) use signals for your own trading only, (c) not redistribute or resell signals.</p>
          <p>We may suspend or terminate access for abuse, fraud, or violation of these terms.</p>
          <p>The service is provided "as is" without warranty. MBE Signal AI is not liable for trading losses.</p>
          <p>Questions? PM <a href="https://t.me/zackmbe" target="_blank" rel="noopener noreferrer" style={{ color: Palette.accent }}>@zackmbe</a> on Telegram.</p>
        </div>
      </Modal>
    </div>
  );
};

export default ProfileScreen;
